import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { MOUSE } from "three";
import { useState } from "react";
import Scene from "./components/Scene";
import FloatingGiftCard from "./components/GiftConfigModal";
import QuizModal from "./components/QuizModal";

interface ClickInfo {
  objectName: string;
  hitPoint: { x: number; y: number; z: number };
  localPosition: { x: number; y: number; z: number };
  worldPosition: { x: number; y: number; z: number };
  clientX: number;
  clientY: number;
}

export default function App() {
  const [clickInfo, setClickInfo] = useState<ClickInfo | null>(null);
  const [selectedGift, setSelectedGift] = useState<string | null>(null);
  const [cakeModalPos, setCakeModalPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [valentineModalPos, setValentineModalPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [teddyBearModalPos, setTeddyBearModalPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [giftModalPos, setGiftModalPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [basketModalPos, setBasketModalPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);

  // Centralized component name definitions
  const giftComponentsNames = ["object_2_2", "object_3_2"];
  const computerComponentsNames = ["materialfbxasc032fbxasc0354"];
  const cakeComponentsNames = [
    "baziercurve",
    "plane001_material",
    "circle_material",
  ];
  const valentinesComponentsNames = [
    "object_11",
    "object_8_1",
    "object_7_1_1",
    "object_9_1_1",
    "object_5",
  ];
  const teddyBearComponentsNames = ["teddy_teddymat_0"];
  const basketComponentsNames = ["straw_basket", "round_towel", "pillow"];

  const handleClickObject = (info: ClickInfo) => {
    setClickInfo(info);

    const objectNameLower = info.objectName.toLowerCase();

    if (
      computerComponentsNames.some((name) => objectNameLower.includes(name))
    ) {
      setShowQuiz(true);
      return;
    }

    if (cakeComponentsNames.some((name) => objectNameLower.includes(name))) {
      setCakeModalPos({ x: info.clientX, y: info.clientY });
      setTimeout(() => setCakeModalPos(null), 2000); // Hide after 3 seconds
      return;
    }

    if (
      valentinesComponentsNames.some((name) => objectNameLower.includes(name))
    ) {
      setValentineModalPos({ x: info.clientX, y: info.clientY });
      setTimeout(() => setValentineModalPos(null), 2000); // Hide after 3 seconds
      return;
    }

    if (
      teddyBearComponentsNames.some((name) => objectNameLower.includes(name))
    ) {
      setTeddyBearModalPos({ x: info.clientX, y: info.clientY });
      setTimeout(() => setTeddyBearModalPos(null), 2000); // Hide after 3 seconds
      return;
    }

    if (basketComponentsNames.some((name) => objectNameLower.includes(name))) {
      setBasketModalPos({ x: info.clientX, y: info.clientY });
      setTimeout(() => setBasketModalPos(null), 2000); // Hide after 3 seconds
      return;
    }

    // Check if clicked object is a gift (only allow if quiz is completed)

    const isGift = giftComponentsNames.some((name) =>
      info.objectName.toLowerCase().includes(name),
    );
    if (isGift) {
      if (!quizCompleted) {
        setGiftModalPos({ x: info.clientX, y: info.clientY });
        setTimeout(() => setGiftModalPos(null), 2000);
        return;
      }
      setGiftOpen(true);
      setSelectedGift(info.objectName);
    }
  };

  const handleCloseCard = () => {
    setSelectedGift(null);
  };

  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
      <Canvas camera={{ position: [1, 2.5, 4.5], fov: 50 }}>
        <color attach="background" args={["#f5f0ff"]} />

        {/* Lights */}
        <ambientLight intensity={1} />
        <pointLight position={[3, 2.5, 4.5]} intensity={10} />

        {/* Controls */}
        <OrbitControls
          enablePan
          enableZoom
          enableRotate
          rotateSpeed={0.5}
          zoomSpeed={5.0}
          panSpeed={4.0}
          target={[0, 1, 0]}
          mouseButtons={{
            LEFT: MOUSE.DOLLY,
            MIDDLE: MOUSE.PAN,
            RIGHT: MOUSE.ROTATE,
          }}
        />

        <Scene
          onClickObject={handleClickObject}
          giftComponentsNames={giftComponentsNames}
          giftOpen={giftOpen}
        />
      </Canvas>

      {/* Floating Gift Card */}
      {selectedGift && <FloatingGiftCard onClose={handleCloseCard} />}

      {/* Quiz Modal */}
      {showQuiz && (
        <QuizModal
          onComplete={() => {
            setQuizCompleted(true);
            setShowQuiz(false);
          }}
          onCancel={() => setShowQuiz(false)}
        />
      )}

      {/* gift Modal */}
      {giftModalPos && (
        <div
          style={{
            position: "fixed",
            left: giftModalPos.x,
            top: giftModalPos.y,
            backgroundColor: "rgba(251, 178, 178, 0.95)",
            color: "#380101",
            padding: "12px 16px",
            borderRadius: "8px",
            fontFamily: "cursive",
            fontSize: "16px",
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(255, 105, 180, 0.5)",
            zIndex: 200,
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
          }}
        >
          Locked!
        </div>
      )}

      {/* Cake Modal */}
      {cakeModalPos && (
        <div
          style={{
            position: "fixed",
            left: cakeModalPos.x,
            top: cakeModalPos.y,
            backgroundColor: "rgba(251, 178, 178, 0.95)",
            color: "#380101",
            padding: "12px 16px",
            borderRadius: "8px",
            fontFamily: "cursive",
            fontSize: "16px",
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(255, 105, 180, 0.5)",
            zIndex: 200,
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
          }}
        >
          🍰 Buy me cake!
        </div>
      )}

      {/* Valentine Modal */}
      {valentineModalPos && (
        <div
          style={{
            position: "fixed",
            left: valentineModalPos.x,
            top: valentineModalPos.y,
            backgroundColor: "rgba(251, 178, 178, 0.95)",
            color: "#380101",
            padding: "12px 16px",
            borderRadius: "8px",
            fontFamily: "cursive",
            fontSize: "16px",
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(255, 105, 180, 0.5)",
            zIndex: 200,
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
          }}
        >
          Love You 💖
        </div>
      )}

      {/* Teddy Bear Modal */}
      {teddyBearModalPos && (
        <div
          style={{
            position: "fixed",
            left: teddyBearModalPos.x,
            top: teddyBearModalPos.y,
            backgroundColor: "rgba(251, 178, 178, 0.95)",
            color: "#380101",
            padding: "12px 16px",
            borderRadius: "8px",
            fontFamily: "cursive",
            fontSize: "16px",
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(255, 105, 180, 0.5)",
            zIndex: 200,
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
          }}
        >
          Don't touch me, you pervert! 🧸
        </div>
      )}

      {/* Basket Modal */}
      {basketModalPos && (
        <div
          style={{
            position: "fixed",
            left: basketModalPos.x,
            top: basketModalPos.y,
            backgroundColor: "rgba(251, 178, 178, 0.95)",
            color: "#380101",
            padding: "12px 16px",
            borderRadius: "8px",
            fontFamily: "cursive",
            fontSize: "16px",
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(255, 105, 180, 0.5)",
            zIndex: 200,
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
          }}
        >
          You owe me laundry service x 50. Heh 🧺
        </div>
      )}

      {clickInfo && (
        <div
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "#00ff00",
            padding: "20px",
            borderRadius: "8px",
            fontFamily: "monospace",
            fontSize: "14px",
            maxWidth: "400px",
            boxShadow: "0 0 20px rgba(0, 255, 0, 0.3)",
            zIndex: 100,
          }}
        >
          <div
            style={{
              marginBottom: "10px",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            👉 Clicked Object
          </div>
          <div>📦 Object: {clickInfo.objectName}</div>
          <div style={{ marginTop: "10px" }}>
            📍 Hit Point:
            <div style={{ marginLeft: "10px", fontSize: "12px" }}>
              x: {clickInfo.hitPoint.x.toFixed(2)}, y:{" "}
              {clickInfo.hitPoint.y.toFixed(2)}, z:{" "}
              {clickInfo.hitPoint.z.toFixed(2)}
            </div>
          </div>
          <div style={{ marginTop: "10px" }}>
            📦 Local Position:
            <div style={{ marginLeft: "10px", fontSize: "12px" }}>
              x: {clickInfo.localPosition.x.toFixed(2)}, y:{" "}
              {clickInfo.localPosition.y.toFixed(2)}, z:{" "}
              {clickInfo.localPosition.z.toFixed(2)}
            </div>
          </div>
          <div style={{ marginTop: "10px" }}>
            🌍 World Position:
            <div style={{ marginLeft: "10px", fontSize: "12px" }}>
              x: {clickInfo.worldPosition.x.toFixed(2)}, y:{" "}
              {clickInfo.worldPosition.y.toFixed(2)}, z:{" "}
              {clickInfo.worldPosition.z.toFixed(2)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
